/**
 * AdminFleetInvoices — list + actions for fleet invoices across all owners.
 *
 * Row tap opens a detail modal with full admin parity:
 *   • Mark Paid / Unmark
 *   • Send / Resend the invoice email
 *   • Open the printable HTML in a new tab
 *   • Delete (with confirm)
 *
 * All write actions hit the owner-side `/api/fleet/invoices/*` endpoints with
 * `?ownerId=` (the invoice's owner) so admin permission middleware applies.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, FlatList, ActivityIndicator, Modal, ScrollView, TouchableOpacity, Platform } from 'react-native';
import { openExternal } from '../../src/utils/openExternal';
import { Ionicons } from '@expo/vector-icons';
import { useLocalSearchParams } from 'expo-router';

import { api } from '../../src/api/client';
import { confirmDestructive } from '../../src/utils/confirm';
import { AdminToolbar } from '../../src/components/AdminList';
import ContentContainer from '../../src/components/ContentContainer';
import PageFooter from '../../src/components/PageFooter';
import Button from '../../src/components/Button';
import { useToast } from '../../src/components/Toast';
import { useTheme } from '../../src/context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';
import { config } from '../../src/config';
import { useModalBottomPadding } from '../../src/utils/useModalBottomPadding';
import { titleCase, formatPhone } from '../../src/utils/format';

interface InvoiceItem {
  readonly id: string;
  readonly invoiceNumber: string;
  readonly companyName: string;
  readonly billingEmail: string | null;
  readonly primaryContactName: string | null;
  readonly primaryContactPhone: string | null;
  readonly totalAmount: number;
  readonly status: string;
  readonly dueDate: string;
  readonly paidAt: string | null;
  readonly businessName: string;
  readonly createdAt: string;
  readonly ownerId: string | null;
}

const STATUS_FILTERS = [
  { label: 'Draft', value: 'draft' },
  { label: 'Sent', value: 'sent' },
  { label: 'Paid', value: 'paid' },
  { label: 'Overdue', value: 'overdue' },
];

const statusColor = (s: string) => {
  if (s === 'paid') return '#059669';
  if (s === 'overdue') return '#dc2626';
  if (s === 'sent') return '#2563eb';
  return '#6b7280';
};

const fmt = (cents: number) => `$${(cents / 100).toFixed(2)}`;

const PAGE_LIMIT = 20;

export default function AdminFleetInvoices() {
  const { ownerId: paramOwnerId } = useLocalSearchParams<{ ownerId?: string }>();
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const toast = useToast();
  const modalBottomPadding = useModalBottomPadding();

  const [invoices, setInvoices] = useState<InvoiceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');

  const [selected, setSelected] = useState<InvoiceItem | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const fetchData = useCallback(async (p = 1, s = '', status = '', signal?: AbortSignal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: String(PAGE_LIMIT) });
      if (s) params.set('search', s);
      if (status) params.set('status', status);
      if (paramOwnerId) params.set('ownerId', paramOwnerId);
      const data = await api<{ invoices: InvoiceItem[]; total: number }>(`/api/admin/fleet/invoices?${params}`);
      if (signal?.aborted) return;
      setInvoices(data.invoices);
      setTotal(data.total);
      setPage(p);
    } catch (err) { if (!(signal?.aborted)) console.error(err); }
    finally { if (!(signal?.aborted)) setLoading(false); }
  }, [paramOwnerId]);

  useEffect(() => { const ac = new AbortController(); fetchData(1, '', '', ac.signal); return () => ac.abort(); }, [paramOwnerId]);

  const csvEndpoint = `/api/admin/fleet/invoices?${new URLSearchParams({ ...(search && { search }), ...(statusFilter && { status: statusFilter }) })}`;

  /** Append ?ownerId=<invoice owner> so the admin's permission check passes upstream. */
  const ownerQS = (inv: InvoiceItem) => inv.ownerId ? `?ownerId=${encodeURIComponent(inv.ownerId)}` : '';

  const refreshAfter = async () => {
    await fetchData(page, search, statusFilter);
    if (selected) {
      try {
        const fresh = await api<{ invoices: InvoiceItem[]; total: number }>(
          `/api/admin/fleet/invoices?${new URLSearchParams({ ownerId: selected.ownerId ?? '', limit: '1', search: selected.invoiceNumber })}`,
        );
        const next = fresh.invoices.find(i => i.id === selected.id);
        if (next) setSelected(next);
      } catch { /* ignore */ }
    }
  };

  const handleMarkPaid = async () => {
    if (!selected || !selected.ownerId) return;
    setActionBusy(true);
    try {
      await api(`/api/fleet/invoices/${selected.id}/mark-paid${ownerQS(selected)}`, { method: 'PATCH', body: {} });
      toast.show({ type: 'success', title: 'Marked paid' });
      await refreshAfter();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally { setActionBusy(false); }
  };

  const handleSend = async () => {
    if (!selected || !selected.ownerId) return;
    setActionBusy(true);
    try {
      await api(`/api/fleet/invoices/${selected.id}/send${ownerQS(selected)}`, { method: 'POST', body: {} });
      toast.show({ type: 'success', title: 'Sent', message: `Invoice emailed to ${selected.billingEmail ?? 'billing contact'}` });
      await refreshAfter();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally { setActionBusy(false); }
  };

  const handleOpenHtml = async () => {
    if (!selected || !selected.ownerId) return;
    setActionBusy(true);
    try {
      const res = await api<{ viewToken: string }>(`/api/fleet/invoices/${selected.id}/view-token${ownerQS(selected)}`, { method: 'POST', body: {} });
      const url = `${config.apiUrl}/api/fleet/invoices/${selected.id}/html?token=${encodeURIComponent(res.viewToken)}`;
      await openExternal(url);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally { setActionBusy(false); }
  };

  const handleDelete = () => {
    if (!selected || !selected.ownerId) return;
    const doDelete = async () => {
      setActionBusy(true);
      try {
        await api(`/api/fleet/invoices/${selected.id}${ownerQS(selected)}`, { method: 'DELETE' });
        toast.show({ type: 'success', title: 'Deleted' });
        setSelected(null);
        await fetchData(page, search, statusFilter);
      } catch (err: any) {
        toast.show({ type: 'error', title: 'Error', message: err.message });
      } finally { setActionBusy(false); }
    };
    confirmDestructive(
      'Delete invoice?',
      `${selected.invoiceNumber} — this can't be undone.`,
      doDelete,
    );
  };

  const renderItem = ({ item }: { item: InvoiceItem }) => (
    <TouchableOpacity style={styles.row} onPress={() => setSelected(item)} activeOpacity={0.7}>
      <View style={styles.rowBody}>
        <View style={styles.rowHeader}>
          <Text style={styles.name}>{item.invoiceNumber}</Text>
          <View style={[styles.badge, { backgroundColor: statusColor(item.status) + '15' }]}>
            <Text style={[styles.badgeText, { color: statusColor(item.status) }]}>{item.status}</Text>
          </View>
        </View>
        <Text style={styles.meta}>{item.companyName} · {fmt(item.totalAmount)} · Due {new Date(item.dueDate).toLocaleDateString()}</Text>
        {(item.billingEmail || item.primaryContactName || item.primaryContactPhone) ? (
          <Text style={styles.sub}>
            {item.billingEmail ?? ''}
            {item.primaryContactName ? ` · ${titleCase(item.primaryContactName)}` : ''}
            {item.primaryContactPhone ? ` · ${formatPhone(item.primaryContactPhone)}` : ''}
          </Text>
        ) : null}
        <Text style={styles.sub}>Owner: {item.businessName}</Text>
      </View>
    </TouchableOpacity>
  );

  return (
    <View style={styles.container}>
      <ContentContainer role="admin" footer={false}>
        <AdminToolbar
          search={search} onSearchChange={setSearch} onSearch={() => fetchData(1, search, statusFilter)}
          total={total} page={page} limit={20} onPageChange={(p) => fetchData(p, search, statusFilter)}
          csvEndpoint={csvEndpoint} csvFilename="fleet-invoices.csv" label="invoice"
          filterOptions={STATUS_FILTERS} activeFilter={statusFilter}
          onFilterChange={(v) => { setStatusFilter(v); fetchData(1, search, v); }}
        />
        {loading ? (
          <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.xl }} />
        ) : (
          <FlatList ListFooterComponent={<PageFooter />} data={invoices} keyExtractor={item => item.id} renderItem={renderItem}
            ListEmptyComponent={<Text style={styles.empty}>No fleet invoices found</Text>}
          />
        )}

        <Modal visible={!!selected} transparent animationType="slide" onRequestClose={() => setSelected(null)}>
          <View style={styles.modalOverlay}>
            <View style={[styles.modalContent, { paddingBottom: modalBottomPadding }]}>
              {selected && (
                <ScrollView showsVerticalScrollIndicator>
                  <View style={styles.modalHeader}>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.modalTitle}>{selected.invoiceNumber}</Text>
                      <Text style={styles.modalSub}>{selected.companyName} · {selected.businessName}</Text>
                    </View>
                    <TouchableOpacity onPress={() => setSelected(null)} hitSlop={12}>
                      <Ionicons name="close" size={24} color={colors.text} />
                    </TouchableOpacity>
                  </View>

                  <View style={[styles.kvBlock, { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm }]}>
                    <View style={[styles.badge, { backgroundColor: statusColor(selected.status) + '15' }]}>
                      <Text style={[styles.badgeText, { color: statusColor(selected.status) }]}>{selected.status}</Text>
                    </View>
                    <Text style={styles.modalValue}>{fmt(selected.totalAmount)}</Text>
                  </View>

                  <KV label="Billing email" value={selected.billingEmail ?? '—'} />
                  <KV label="Contact" value={selected.primaryContactName ? titleCase(selected.primaryContactName) : '—'} />
                  <KV label="Contact phone" value={selected.primaryContactPhone ? formatPhone(selected.primaryContactPhone) : '—'} />
                  <KV label="Due" value={new Date(selected.dueDate).toLocaleDateString()} />
                  {selected.paidAt ? <KV label="Paid" value={new Date(selected.paidAt).toLocaleString()} /> : null}
                  <KV label="Created" value={new Date(selected.createdAt).toLocaleString()} />

                  <Text style={[styles.modalLabel, { marginTop: spacing.md }]}>Actions</Text>
                  <View style={styles.actionGrid}>
                    <Button title="Open PDF/HTML" variant="outline" onPress={handleOpenHtml} loading={actionBusy}
                      icon={<Ionicons name="document-text-outline" size={16} color={colors.primary} />} />
                    {selected.status !== 'paid' && (
                      <Button title="Mark Paid" onPress={handleMarkPaid} loading={actionBusy}
                        icon={<Ionicons name="checkmark-circle-outline" size={16} color={colors.white} />} />
                    )}
                    <Button title={selected.status === 'sent' ? 'Resend' : 'Send'} variant="outline" onPress={handleSend} loading={actionBusy}
                      icon={<Ionicons name="mail-outline" size={16} color={colors.primary} />} />
                    <Button title="Delete" variant="danger" onPress={handleDelete} loading={actionBusy}
                      icon={<Ionicons name="trash-outline" size={16} color={colors.white} />} />
                  </View>

                  <TouchableOpacity style={styles.closeBtn} onPress={() => setSelected(null)}>
                    <Text style={styles.closeBtnText}>Close</Text>
                  </TouchableOpacity>
                </ScrollView>
              )}
            </View>
          </View>
        </Modal>
      </ContentContainer>
    </View>
  );
}

function KV({ label, value }: { label: string; value: string }) {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  return (
    <View style={styles.kvRow}>
      <Text style={styles.modalLabel}>{label}</Text>
      <Text selectable style={styles.modalValue}>{value}</Text>
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },
  row: { paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: c.borderLight },
  rowBody: { flex: 1 },
  rowHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  name: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  badge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 8 },
  badgeText: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, textTransform: 'capitalize' },
  meta: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  sub: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },
  empty: { textAlign: 'center', color: c.textSecondary, marginTop: spacing.xl, fontSize: fontSize.md },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', justifyContent: 'center', alignItems: 'center', padding: spacing.md },
  modalContent: { backgroundColor: c.surface, borderRadius: borderRadius.lg, padding: spacing.lg, width: '100%', maxWidth: 560, maxHeight: '90%', elevation: 5 },
  modalHeader: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: spacing.md },
  modalTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text },
  modalSub: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  modalLabel: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.textSecondary, marginTop: spacing.sm, textTransform: 'uppercase', letterSpacing: 0.5 },
  modalValue: { fontSize: fontSize.sm, color: c.text, marginTop: 2 },
  kvBlock: { marginBottom: spacing.sm },
  kvRow: { marginBottom: spacing.xs },
  actionGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginTop: spacing.xs },
  closeBtn: { marginTop: spacing.lg, paddingVertical: spacing.sm, alignItems: 'center', borderWidth: 1, borderColor: c.border, borderRadius: borderRadius.md },
  closeBtnText: { color: c.textSecondary, fontSize: fontSize.sm, fontWeight: fontWeight.medium },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
